import { motion, useReducedMotion } from "framer-motion";
import { Search } from "lucide-react";
import { HeroScene, HeroSceneSimple } from "@/components/silver/hero/HeroScene";
import { HeroTags } from "@/components/silver/hero/HeroTags";
import KeyInsights from "@/components/silver/proof/KeyInsights";
import { focusPostcodeSearch } from "@/context/SilverContext";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";

const EASE = [0.22, 1, 0.36, 1];
const HEADLINE = [
  ...["How", "many"].map((w) => ({ w })),
  { w: "downsizers", lime: true },
  ...["are", "in", "your", "area?"].map((w) => ({ w })),
];

// NicheCom's campaign photograph as the full hero backdrop, graded into the brand greens.
// Nudged right on desktop so the couple sits between the headline and the animated scene.
function PhotoBackdrop({ reduce, isDesktop }) {
  return (
    <div className="pointer-events-none absolute inset-0 -z-10 bg-[#0C2010]" aria-hidden="true" data-testid="hero-photo-backdrop"
      style={{ maskImage: "linear-gradient(to bottom, black 0%, black 72%, transparent 100%)", WebkitMaskImage: "linear-gradient(to bottom, black 0%, black 72%, transparent 100%)" }}>
      <motion.img src="/brand/couple.jpg?v=2" srcSet="/brand/couple-sm.jpg?v=2 800w, /brand/couple.jpg?v=2 1600w" sizes="100vw" alt=""
        className="absolute inset-y-0 left-0 h-full w-full object-cover lg:inset-y-auto lg:left-auto lg:right-0 lg:top-0 lg:h-auto lg:w-[70%] lg:max-w-none"
        style={isDesktop ? { maskImage: "linear-gradient(to right, transparent 0%, black 28%)", WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 28%)" } : { objectPosition: "60% 18%" }}
        initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 2.6, ease: EASE }} />
      <div className="absolute inset-0 bg-[#3E7A46] opacity-30 mix-blend-multiply" />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(12,32,16,0.97)_0%,rgba(12,32,16,0.95)_26%,rgba(12,32,16,0.38)_48%,rgba(12,32,16,0.22)_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(12,32,16,0.5)_0%,rgba(12,32,16,0)_14%,rgba(12,32,16,0)_38%,rgba(12,32,16,0.72)_62%,rgba(12,32,16,0.96)_100%)]" />
      <div className="absolute inset-0 bg-[#0C2010]/45 lg:hidden" />
      <div className="absolute inset-0 hidden bg-[radial-gradient(ellipse_at_72%_80%,rgba(10,28,14,0.6),transparent_52%)] lg:block" />
      <div className="absolute -bottom-[20%] left-[-30%] right-[-30%] h-[70%] opacity-50"
        style={{
          backgroundImage: "linear-gradient(rgba(198,238,90,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(198,238,90,0.07) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          transform: "perspective(900px) rotateX(64deg)",
          transformOrigin: "50% 100%",
          maskImage: "radial-gradient(ellipse at 60% 20%, black 10%, transparent 65%)",
          WebkitMaskImage: "radial-gradient(ellipse at 60% 20%, black 10%, transparent 65%)",
          animation: reduce ? "none" : "grid-pan 14s linear infinite",
        }} />
    </div>
  );
}

export default function Hero() {
  const reduce = useReducedMotion();
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  return (
    <section id="top" className="relative isolate" data-testid="hero-section">
      <div className="relative isolate overflow-hidden">
      <PhotoBackdrop reduce={reduce} isDesktop={isDesktop} />

      <div className="relative">
        {isDesktop && (
          <motion.div className="absolute inset-y-0 right-0 hidden w-[60%] lg:block" initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 2.2, ease: EASE, delay: 0.3 }}
            style={{ maskImage: "linear-gradient(to right, transparent 0%, black 22%)", WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 22%)" }}>
            <div className="absolute inset-0 pb-6 pt-20" style={{ filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.85))" }}>
              <HeroScene reduce={!!reduce} />
            </div>
            <HeroTags />
          </motion.div>
        )}

        <div className="relative mx-auto flex max-w-7xl flex-col justify-center px-5 pb-20 pt-28 sm:px-8 lg:min-h-[66svh] lg:pb-36 lg:pt-32">
        <div className="max-w-[660px]">
          <h1 aria-label={HEADLINE.map((h) => h.w).join(" ")} className="font-extrabold leading-[1.06] tracking-[-0.04em] text-white [text-shadow:0_2px_28px_rgba(0,0,0,0.45)]" style={{ fontSize: "clamp(2.1rem, 3.3vw, 3.25rem)" }} data-testid="hero-headline">
            {HEADLINE.map(({ w, lime }, i) => (
              <motion.span key={i} className={cn("inline-block pr-[0.24em]", lime && "text-[#C6EE5A] text-glow")} initial={{ opacity: 0, y: 24, filter: "blur(10px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} transition={{ duration: 1, ease: EASE, delay: 0.15 + i * 0.06 }}>
                {w}
              </motion.span>
            ))}
          </h1>
          <motion.p className="mt-5 max-w-[600px] text-base font-medium leading-relaxed text-white/90 sm:text-[18px] [text-shadow:0_1px_12px_rgba(0,0,0,0.5)]" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 0.95 }} data-testid="hero-subline">
            37% of UK homebuyers in 2025 were downsizers. Enter your postcodes and see exactly how many live near you, and what it costs to write to them.
          </motion.p>
          <motion.div className="mt-7 flex flex-wrap items-center gap-3" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 1.15 }}>
            <button type="button" className="btn-primary text-[15px]" onClick={focusPostcodeSearch} data-testid="hero-explore-area-btn">
              <Search className="h-4 w-4" strokeWidth={2.5} /> See Who You're Missing
            </button>
          </motion.div>
        </div>

        {!isDesktop && (
          <div className="relative mt-10 h-[240px] sm:h-[320px]" data-testid="hero-scene-mobile">
            <HeroSceneSimple />
          </div>
        )}
        </div>
      </div>
      </div>

      <KeyInsights />
    </section>
  );
}
